/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {CareFieldVal, getNewCareField, wellnessCoachElements} from "@app/app-shared/care_config";
import {ThunderDispatcher, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {_clearTimeout, _setTimeout, Module, Second} from "@intuitionrobotics/ts-common";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {ApiCreateUnitGoal, ApiListUnitGoals, UICareGoal, UnitCareGoal} from "@app/app-shared/api";

export interface OnCareConfigQueryReturned {
    __onCareConfigQueryReturned: () => void;
}

export interface OnCareConfigEntryUpdated {
    __onCareConfigEntryUpdated: (created: boolean, success: boolean) => void;
}

export interface OnCareConfigRemotelyChanged {
    __onCareConfigRemotelyChanged: (doc?: UnitCareGoal) => void;
}

export interface OnCareConfigUpsertEventually {
    __onCareConfigUpsertEventually: (patientId: string, latest: boolean, error?: boolean) => void;
}

export interface OnCareConfigJobUpdate {
    __onCareConfigJobUpdate: () => void;
}

export const dispatch_onCareConfigQueryReturned = new ThunderDispatcher<OnCareConfigQueryReturned, "__onCareConfigQueryReturned">(
    "__onCareConfigQueryReturned");
export const dispatch_onCareConfigUpdated = new ThunderDispatcher<OnCareConfigEntryUpdated, "__onCareConfigEntryUpdated">("__onCareConfigEntryUpdated");

export const dispatch_onCareConfigUpsertEventually = new ThunderDispatcher<OnCareConfigUpsertEventually, "__onCareConfigUpsertEventually">(
    "__onCareConfigUpsertEventually");

export const dispatch_onCareConfigJobUpdate = new ThunderDispatcher<OnCareConfigJobUpdate, "__onCareConfigJobUpdate">(
    "__onCareConfigJobUpdate");

export enum EventuallyStateStatus {
    Synced = "Synced",
    Saving = "Saving",
    Error = "Error",
}

export enum EventuallyUpsertStatus {
    Pending = "Pending",
    Queued = "Queued",
    Running = "Running",
    Error = "Error",
}

type UpsertJob = {
    _status: EventuallyUpsertStatus
    _timer?: number
}

type Config = {
    config: string
}

export type UnitGoalMap = { [goalName: string]: UICareGoal | undefined };
export type CareGoalsMap = { [unitId: string]: (UnitGoalMap) | undefined };

export class GoalsConfigModule_Class
    extends Module<Config> {

    private configs: CareGoalsMap = {};
    private configsJobs: (UnitCareGoal & UpsertJob)[] = [];

    constructor() {
        super("GoalsConfigModule");
        this.retryCount = {
            list: {
                maxTries: 5,
                counter: 0
            },
            create: {
                maxTries: 5,
                counter: 0
            }

        };
    }

    private readonly retryCount: {
        [key: string]: {
            maxTries: number
            counter: number
        } | undefined
    };

    private resetRetries = (apiDef: { key: string }) => {
        const retryCountElement = this.retryCount[apiDef.key];
        if (!retryCountElement?.counter)
            return;

        retryCountElement.counter = 0;
    };

    private increaseRetries = (key: string) => {
        const retryCountElement = this.retryCount[key];
        if (!retryCountElement)
            return;

        retryCountElement.counter++;
    };

    private shouldIRetry = (key: string): boolean => {
        const retryCountElement = this.retryCount[key];
        if (!retryCountElement)
            return false;

        return retryCountElement.counter < retryCountElement.maxTries;
    };

    getByUnitId = (unitId: string): UICareGoal[] => {
        if (!unitId)
            return [];

        const goalsMap = this.configs[unitId];
        if (!goalsMap)
            return [];

        return Object.values(goalsMap) as UICareGoal[];
    };


    getLocalConfigsPatient = (unitId: string): UICareGoal[] => {
        return this.getJobsByPatient(unitId).reduce((carry, job) => {
            const {_status, _timer, ...config} = job;
            if (!carry)
                return [config];

            const idx = carry.findIndex(c => c.name === job.name);
            if (idx === -1 && !config.enabled)
                return carry;

            if (idx === -1) {
                carry[carry.length] = config;
                return carry;
            }

            if (!config.enabled) {
                carry.splice(idx, 1);
                return carry;
            }

            carry[idx] = config;
            return carry;
        }, this.getByUnitId(unitId));
    };

    private removeJob = (unitId: string, goalName: string) => {
        this.configsJobs = this.configsJobs.filter(j => j.unitId !== unitId || j.name !== goalName);
    };

    private promoteJob = (response: UnitCareGoal) => {
        const job = this.getJob(response.unitId, response.name);
        if (!job) {
            // dispatch_onCareConfigJobUpdate.dispatchUI();
            // dispatch_onCareConfigUpsertEventually.dispatchUI(patientId, true);
            return;
        }

        const {_status, _timer, ..._config} = job;
        switch (job._status) {
            case EventuallyUpsertStatus.Pending:
                // Basically some other call is about to be fired..
                break;

            case EventuallyUpsertStatus.Running:
                // job completed successfully
                this.removeJob(response.unitId, response.name);
                // dispatch_onCareConfigJobUpdate.dispatchUI();
                break;

            case EventuallyUpsertStatus.Queued:
                this.removeJob(response.unitId, response.name);
                this.upsertEventually(_config);
                break;

            case EventuallyUpsertStatus.Error:
                // dispatch_onCareConfigJobUpdate.dispatchUI();
                return;

            default:
                return;
        }
    };

    private getJob = (unitId: string, name: string) => this.configsJobs.find(j => j.name === name && j.unitId === unitId);

    public getJobsByPatient = (unitId: string) => this.configsJobs.filter(j => j.unitId === unitId);
    private upsertCareConfigJob = (careConfigJob: UnitCareGoal & UpsertJob) => {
        const itemIdx = this.configsJobs.findIndex(_item => _item.unitId === careConfigJob.unitId && _item.name === careConfigJob.name);
        const idx = itemIdx !== -1 ? itemIdx : this.configsJobs.length;
        this.configsJobs[idx] = careConfigJob;
        dispatch_onCareConfigJobUpdate.dispatchUI();
        return this.configsJobs;
    };

    private upsertEventually = (config_: UnitCareGoal) => {
        const job = this.getJob(config_.unitId, config_.name);
        if (job) {
            switch (job._status) {
                case EventuallyUpsertStatus.Pending:
                    _clearTimeout(job._timer);
                    break;
                case EventuallyUpsertStatus.Running:
                case EventuallyUpsertStatus.Queued:
                    const _careConfigJob = {
                        ...config_,
                        _status: EventuallyUpsertStatus.Queued
                    };
                    this.upsertCareConfigJob(_careConfigJob);
                    return;
                case EventuallyUpsertStatus.Error:
                    // basically try again
                    break;

                default:
                    return this.logInfo(`Unknown status ${job._status}`);
            }
        }
        const handler = () => {
            const _job = this.getJob(config_.unitId, config_.name);
            if (!_job)
                return this.logWarning("Something is very off. Check your code!");

            const {unitId, name, windowType, threshold, enabled, ranges} = _job;
            this.upsert(unitId, {name, windowType, threshold, enabled, unitId, ranges}); //look like in the original create we were sending job data need to understand how to add this.
            this.upsertCareConfigJob(
                {
                    ...config_,
                    _status: EventuallyUpsertStatus.Running,
                    _timer: undefined
                });
        };

        const careConfigJob = {
            ...config_,
            _status: EventuallyUpsertStatus.Pending,
            _timer: _setTimeout(handler, Second)
        };
        this.upsertCareConfigJob(careConfigJob);
    };

    onConfigUpdate = (name: string, val: CareFieldVal, unitId: string, demo: boolean) => {

        const objectSubType = wellnessCoachElements.includes(name)
            ? AnalyticConsts.ObjectSubTypes.WELLNESS_PROGRAM
            : AnalyticConsts.ObjectSubTypes.CARE_PLAN;

        AnalyticsModule.logCareManagerEvent({
            objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
            entity: AnalyticConsts.Entities.USER,
            activityStatus: val.enabled.toString(),
            objectSubType,
            activityDetails: name,
            frequency: val.frequency.value,
            frequencyValue: val.frequency.times
        });

        const updatedCareGoal = getNewCareField(name, val);
        if (demo) {
            this.addOrRemoveCareGoal(unitId, {...updatedCareGoal, unitId});
            dispatch_onCareConfigUpdated.dispatchUI(false, true);
            return;
        }

        this.upsertEventually({unitId, ...updatedCareGoal});
    };

    upsert = (unitId: string, goal: UnitCareGoal) => {
        XhrHttpModule
            .createRequest<ApiCreateUnitGoal>(HttpMethod.POST, "upsert")
            .setLabel(`Update ${goal.name} for unit:${unitId}`)
            .setRelativeUrl(`/v1/goal/upsert`)
            .setJsonBody(goal)
            .setOnError(() => {
                this.logError(`Something went wrong with updating the Care Configuration for unit:${unitId} : ""}`);
                const careConfigJob = this.getJob(unitId, goal?.name);
                if (!careConfigJob)
                    return true;

                this.upsertCareConfigJob(
                    {
                        ...careConfigJob,
                        _status: EventuallyUpsertStatus.Error
                    }
                );
                dispatch_onCareConfigUpdated.dispatchUI(false, false);
                dispatch_onCareConfigUpsertEventually.dispatchUI(unitId, false, true);
                // ErrorToast("Something went wrong with updating the Care Configuration");
                return true;
            })
            .execute(() => {
                this.addOrRemoveCareGoal(unitId, goal)
                this.promoteJob(goal);
                dispatch_onCareConfigUpdated.dispatchUI(false, true);
            });

    };
    //get all goals for a specific unit
    list = (unitId: string) => {
        const apiDef = {key: "list"};
        const key = "list-goals";

        XhrHttpModule
            .createRequest<ApiListUnitGoals>(HttpMethod.GET, key)
            .setUrlParams({unitId})
            .setRelativeUrl(`/v1/goal/list`)
            .setLabel(`Listing Care Configs`)
            .setOnError(() => {
                if (this.shouldIRetry(key)) {
                    this.increaseRetries(key);
                    _setTimeout(() => this.list(unitId), 2 * Second);
                }
                return true;
            })
            .execute(async (response: UICareGoal[]) => {
                this.resetRetries(apiDef);
                response.forEach((careGoal) => {
                    this.addCareGoal(unitId, careGoal);
                });
                dispatch_onCareConfigQueryReturned.dispatchUI();
            });
    };

    public addCareGoal(unitId: string, careGoal: UICareGoal) {
        this.configs[unitId] = {
            ...this.configs[unitId],
            [careGoal.name]: careGoal
        };
    }

    private addOrRemoveCareGoal(unitId: string, careGoal: UnitCareGoal) {
        //this will override a goal if it's exist in the map.
        const unitConfig = this.configs[unitId];
        if (careGoal.enabled)
            return this.addCareGoal(unitId, careGoal)

        // If the goal has been disabled, delete entry from map
        if (unitConfig)
            delete unitConfig[careGoal.name]
    }

    public setCareGoals = (data: CareGoalsMap) => {
        this.configs = data;
        dispatch_onCareConfigQueryReturned.dispatchUI();
    }

}

export const GoalsConfigModule = new GoalsConfigModule_Class();
